/**
 * Shared navigation configuration hook for the V1.6 redesign
 *
 * Provides a single source of truth for navigation items used by
 * the Tabs layout, LeftSidebar, and sidebar bottom action bar.
 * @module hooks/useNavItems
 */

import { usePathname } from "expo-router";
import {
  Bell,
  Columns3,
  Crosshair,
  Home,
  Plus,
  Search,
  Settings,
  Sparkles,
  Users,
} from "lucide-react-native";

import UsergroupIcon from "@/features/squad-planning/icons/UsergroupIcon";
import React, { useMemo } from "react";

import { POSTHOG_FLAGS } from "@/config/featureFlags";
import { useCurrentUser } from "@/features/auth/providers/CurrentUser";
import { DEFAULT_ORGANIZATION_ID } from "@/features/knowledge-graph/constants";
import { useIsPipelineSurfaceActive } from "@/features/player-kanban/hooks/useIsPipelineSurfaceActive";
import { usePipelineTierBlock } from "@/features/player-kanban/hooks/usePipelineTierBlock";
import { useNavigationContext } from "@/providers/NavigationProvider";
import type { NavigationSource } from "@/providers/NavigationProvider";

import useIsManagerPlus from "./useIsManagerPlus";
import useMobileScreen from "./useMobileScreen";
import { usePostHogIdentified } from "./posthogIdentityStore";
import usePostHogFeatureFlag from "./usePostHogFeatureFlag";

/**
 * Navigation item configuration
 */
interface NavItemConfig {
  /** Unique identifier for the nav item */
  readonly id: string;
  /** Route name for Tabs.Screen (matches file/directory name in (tabs)) */
  readonly routeName: string;
  /** Full path for router.push navigation */
  readonly path: string;
  /** Display label */
  readonly label: string;
  /** Icon component from lucide-react-native */
  readonly icon: React.ElementType;
  /** Whether to show in tab bar on small screens */
  readonly showInTabsSmallScreen: boolean;
  /** Whether to show in tab bar on large screens */
  readonly showInTabsLargeScreen: boolean;
  /** Whether to show in left sidebar main section */
  readonly showInSidebar: boolean;
  /** Whether to show in left sidebar bottom action bar */
  readonly showInSidebarBottom: boolean;
  /** Accessibility label for screen readers */
  readonly accessibilityLabel: string;
  /** When set, item is only visible when this PostHog feature flag is enabled */
  readonly featureFlag?: string;
  /** When true, item is hidden for users in the default organization */
  readonly hideForDefaultOrg?: boolean;
  /**
   * When true, the item is visible only to Owner/Admin/Manager (SE-6936).
   *
   * Unlike `featureFlag`, this gate FAILS CLOSED: it hides the item until the
   * identity resolves and proves Manager+. A feature flag is a rollout
   * toggle and is optimistic by design (see `isNavItemVisible`); a role is an
   * authorization fact and must not be optimistic, or the item flashes for
   * exactly the users it exists to hide it from.
   */
  readonly requiresManagerPlus?: boolean;
  /**
   * Badge text to show on this item, e.g. "BETA". Rendered unconditionally by
   * the left sidebar whenever it is set. Intentionally retained with no
   * consumer today (SE-7642 removed Squad's, the last one) so the next surface
   * shipping behind a badge can just declare it.
   */
  readonly badgeText?: string;
}

/**
 * Navigation item with computed active state
 */
interface NavItem extends NavItemConfig {
  /** Whether this route is currently active */
  readonly isActive: boolean;
  /** Resolved badge text (present only when the item defines badgeText) */
  readonly badge?: string;
}

/**
 * Return type for useNavItems hook
 */
interface UseNavItemsReturn {
  /** All navigation items with active state computed */
  readonly allItems: readonly NavItem[];
  /** Items filtered for tab bar display (respects screen size) */
  readonly tabItems: readonly NavItem[];
  /** Items filtered for sidebar main section display */
  readonly sidebarItems: readonly NavItem[];
  /** Items filtered for sidebar bottom action bar display */
  readonly sidebarBottomItems: readonly NavItem[];
  /** Whether the current screen is small (mobile) */
  readonly isSmallScreen: boolean;
}

/**
 * Static navigation configuration - single source of truth for V1.6 redesign
 */
const NAV_CONFIG: readonly NavItemConfig[] = [
  {
    id: "home",
    routeName: "index",
    path: "/",
    label: "Home",
    icon: Home,
    showInTabsSmallScreen: true,
    showInTabsLargeScreen: true,
    showInSidebar: true,
    showInSidebarBottom: false,
    accessibilityLabel: "Navigate to Home",
  },
  {
    id: "scouting",
    routeName: "scouting",
    path: "/scouting",
    label: "Scouting",
    icon: Crosshair,
    showInTabsSmallScreen: true,
    showInTabsLargeScreen: true,
    showInSidebar: true,
    showInSidebarBottom: false,
    accessibilityLabel: "Navigate to Scouting",
  },
  {
    id: "squad",
    routeName: "squad",
    path: "/squad",
    label: "Squad",
    icon: UsergroupIcon,
    showInTabsSmallScreen: true,
    showInTabsLargeScreen: true,
    showInSidebar: true,
    showInSidebarBottom: false,
    accessibilityLabel: "Navigate to Squad",
    // Squad left beta (SE-7642) — no `badgeText` here any more.
    featureFlag: POSTHOG_FLAGS.SQUAD_ROLE_GATING,
    // SE-6936: the flag above is a ROLLOUT toggle whose "role targeting" lived
    // in the PostHog dashboard. It resolved `true` for MEMBER +10000000099 on
    // staging, so the tab rendered for a MEMBER. The role is now asserted in
    // code, fail-closed, next to the rollout toggle rather than inside it.
    requiresManagerPlus: true,
  },
  {
    id: "ask-gemini",
    routeName: "knowledge-graph",
    path: "/knowledge-graph",
    label: "Ask Gemini AI",
    icon: Sparkles,
    showInTabsSmallScreen: true,
    showInTabsLargeScreen: false,
    showInSidebar: true,
    showInSidebarBottom: false,
    accessibilityLabel: "Navigate to Ask Gemini AI",
    hideForDefaultOrg: true,
  },
  {
    id: "watchlist",
    routeName: "watchlist",
    path: "/watchlist",
    label: "Watchlist",
    icon: Users,
    showInTabsSmallScreen: false,
    showInTabsLargeScreen: false,
    showInSidebar: false,
    showInSidebarBottom: false,
    accessibilityLabel: "Navigate to Watchlist",
  },
  {
    id: "search",
    routeName: "search",
    path: "/search",
    label: "Search",
    icon: Search,
    showInTabsSmallScreen: true,
    showInTabsLargeScreen: false,
    showInSidebar: false,
    showInSidebarBottom: false,
    accessibilityLabel: "Navigate to Search",
  },
  {
    id: "add",
    routeName: "add",
    path: "",
    label: "Add",
    icon: Plus,
    showInTabsSmallScreen: false,
    showInTabsLargeScreen: false,
    showInSidebar: false,
    showInSidebarBottom: true,
    accessibilityLabel: "Add",
  },
  {
    id: "notifications",
    routeName: "notifications",
    path: "",
    label: "Notifications",
    icon: Bell,
    showInTabsSmallScreen: false,
    showInTabsLargeScreen: false,
    showInSidebar: false,
    showInSidebarBottom: true,
    accessibilityLabel: "Notifications",
  },
  {
    id: "settings",
    routeName: "settings",
    path: "",
    label: "Settings",
    icon: Settings,
    showInTabsSmallScreen: false,
    showInTabsLargeScreen: false,
    showInSidebar: false,
    showInSidebarBottom: true,
    accessibilityLabel: "Settings",
  },
];

/**
 * Top-level Pipeline nav entry (SE-5509).
 *
 * Promotes the pipeline surface from the legacy Scouting > Boards tab to its
 * own primary nav section. It points at the EXISTING `/player-kanban` route,
 * which SE-6071 already renders as the pipeline surface for Manager+ users —
 * no new `/pipeline` route is created, and `/player-kanban` stays reachable by
 * direct URL as the documented rollback path.
 *
 * This entry is injected into the effective nav config ONLY when the pipeline
 * surface is active (see {@link useIsPipelineSurfaceActive}); it is not part of
 * the static `NAV_CONFIG`, so the production nav (flag off) is byte-for-byte
 * unchanged. It shows in the desktop sidebar and large-screen tab set but not
 * in the small-screen tab bar: the pipeline board is a wide/desktop web
 * surface (like the Boards tab, gated on `IS_WEB && !isSmallScreen`), so it is
 * kept out of the crowded mobile tab bar rather than dropping mobile users on a
 * board surface that has no phone layout.
 */
const PIPELINE_NAV_ITEM: NavItemConfig = {
  id: "pipeline",
  routeName: "player-kanban",
  path: "/player-kanban",
  label: "Pipeline",
  /* Figma 80:15092 — the rail's pipeline slot is lucide `columns-3` (a framed
     three-column board), not `kanban`. */
  icon: Columns3,
  showInTabsSmallScreen: false,
  showInTabsLargeScreen: true,
  showInSidebar: true,
  showInSidebarBottom: false,
  accessibilityLabel: "Navigate to Pipeline",
};

/**
 * Inserts a nav item immediately after the entry with the given id, or at the
 * end of the config when the anchor is absent.
 * @param config - The config to insert into
 * @param anchorId - Id of the item to insert after
 * @param item - The item to insert
 * @returns A new config with the item inserted
 */
function insertAfter(
  config: readonly NavItemConfig[],
  anchorId: string,
  item: NavItemConfig
): readonly NavItemConfig[] {
  const anchorIndex = config.findIndex(entry => entry.id === anchorId);
  const insertAt = anchorIndex === -1 ? config.length : anchorIndex + 1;
  return [...config.slice(0, insertAt), item, ...config.slice(insertAt)];
}

/**
 * Hook providing shared navigation configuration for Tabs and LeftSidebar
 * @returns Navigation items filtered by context (tabs vs sidebar vs sidebar-bottom) and screen size
 * @example
 * // In Tabs layout
 * const { tabItems, isSmallScreen } = useNavItems();
 * tabItems.map(item => (
 *   <Tabs.Screen
 *     name={item.routeName}
 *     options={{ href: shouldShow ? undefined : null }}
 *   />
 * ))
 * @example
 * // In LeftSidebar
 * const { sidebarItems, sidebarBottomItems } = useNavItems();
 * sidebarItems.map(item => (
 *   <NavButton icon={item.icon} label={item.label} />
 * ))
 * sidebarBottomItems.map(item => (
 *   <NavButton icon={item.icon} label={item.label} />
 * ))
 */
/**
 * Routes that belong to the Scouting section for active state detection.
 *
 * `/fixture-planning` joined the list in SE-7326. The surface became a Scouting
 * tab, but the route stayed a real destination — `New report -> Match report`
 * pushes it and the capture screen replaces back to it — so without this entry
 * the rail would highlight nothing on the surface it just sent the user to.
 * That covers both of its views, calendar and list, because they are one route
 * forked by width rather than two.
 */
const SCOUTING_ROUTES = [
  "/scouting",
  "/fixture-planning",
  "/players",
  "/player-search",
  "/player-filter",
  "/player-kanban",
];

/**
 * Whether the pathname is a player detail page reached from the squad table,
 * in which case the Squad nav item owns the highlight instead of Scouting
 * (SE-5350 — "sidebar lies" audit Bug 6).
 * @param pathname - Current pathname from router
 * @param navigationSource - Entry point recorded by NavigationProvider
 * @returns Whether the squad section owns this player page
 */
function isSquadOwnedPlayerPage(
  pathname: string,
  navigationSource: NavigationSource
): boolean {
  return (
    navigationSource === "squad-planning" &&
    (pathname === "/players" || pathname.startsWith("/players/"))
  );
}

/**
 * Whether the pathname is the promoted Pipeline surface (`/player-kanban`).
 * When the pipeline surface is active this route is owned by the top-level
 * Pipeline nav entry (SE-5509), not by Scouting — even though it stays in
 * SCOUTING_ROUTES for the legacy (flag-off) Boards-tab behavior.
 * @param pathname - Current pathname from router
 * @returns Whether the pathname is the player-kanban route or a sub-route
 */
function isPipelinePath(pathname: string): boolean {
  return (
    pathname === "/player-kanban" || pathname.startsWith("/player-kanban/")
  );
}

/**
 * Checks if a given path is currently active based on the pathname
 * @param path - The route path to check
 * @param pathname - Current pathname from router
 * @param navigationSource - Entry point for player-page sub-navigation (SE-5350)
 * @param isPipelineSurfaceActive - Whether the pipeline surface is active; when
 *   true the `/player-kanban` route is owned by the Pipeline entry, so Scouting
 *   yields the highlight there (SE-5509)
 * @returns Whether the path is active
 */
function checkIsActive(
  path: string,
  pathname: string,
  navigationSource: NavigationSource = null,
  isPipelineSurfaceActive = false
): boolean {
  // Home is active on exact match or /home
  if (path === "/") {
    return pathname === "/" || pathname === "/home";
  }
  // Scouting is active for all player-related routes, unless the player
  // page was entered from the squad table (Squad owns the highlight then).
  // When the pipeline surface is active, the /player-kanban route is owned by
  // the top-level Pipeline entry, so Scouting yields the highlight there.
  if (path === "/scouting") {
    if (isSquadOwnedPlayerPage(pathname, navigationSource)) return false;
    if (isPipelineSurfaceActive && isPipelinePath(pathname)) return false;
    return SCOUTING_ROUTES.some(
      route => pathname === route || pathname.startsWith(`${route}/`)
    );
  }
  // Squad also claims player pages entered from the squad table
  if (path === "/squad" && isSquadOwnedPlayerPage(pathname, navigationSource)) {
    return true;
  }
  // Other routes are active if pathname matches or starts with path/
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * Resolved PostHog flag values keyed by flag name.
 *
 * `true` / `false` / `string` are loaded values; `undefined` means the flag
 * has not yet resolved from the PostHog server.
 */
type FlagValues = ReadonlyMap<string, boolean | string | undefined>;

/**
 * Checks whether a nav item is visible given the current user context.
 *
 * Loading state is optimistic: an unresolved flag (`undefined`) keeps the
 * item visible so the sidebar/tab bar does not flicker on cold start.
 * A gated item is hidden only after its flag resolves to an explicit `false`.
 * A `requiresManagerPlus` item is the exception to that optimism: a role gate
 * fails CLOSED, so the item stays hidden until the identity resolves and
 * proves Manager+ (SE-6936).
 * @param config - The nav item configuration
 * @param isDefaultOrgUser - Whether the user belongs to the default organization
 * @param flagValues - Resolved PostHog flag values keyed by flag name
 * @param isManagerPlus - Whether the current user is Owner/Admin/Manager
 * @returns Whether the item should be shown
 */
function isNavItemVisible(
  config: NavItemConfig,
  isDefaultOrgUser: boolean,
  flagValues: FlagValues,
  isManagerPlus: boolean
): boolean {
  if (config.hideForDefaultOrg && isDefaultOrgUser) return false;
  if (config.requiresManagerPlus && !isManagerPlus) return false;
  if (config.featureFlag && flagValues.get(config.featureFlag) === false) {
    return false;
  }
  return true;
}

/**
 * Provides navigation items filtered by context and screen size
 *
 * Central hook for navigation configuration used by tab bar, sidebar main
 * section, and sidebar bottom action bar. Computes active state for each
 * item based on current pathname.
 * @returns Navigation items, filtered lists for tabs/sidebar/sidebar-bottom, and screen size flag
 */
function useNavItems(): UseNavItemsReturn {
  const pathname = usePathname();
  const isSmallScreen = useMobileScreen();
  const currentUser = useCurrentUser();
  const { navigationSource } = useNavigationContext();
  // Strict equality: undefined (loading) and null (errored) do NOT count as
  // default-org, so org-gated items stay optimistically visible while auth
  // resolves rather than flickering off then on. (SE-5123)
  const isDefaultOrgUser =
    currentUser?.organizationId === DEFAULT_ORGANIZATION_ID;
  const squadRoleGatingValue = usePostHogFeatureFlag(
    POSTHOG_FLAGS.SQUAD_ROLE_GATING
  );
  // SE-5509: promote Pipeline to a top-level nav section when the pipeline
  // surface (SE-6071) is active for this user. Off in production, so nav is
  // unchanged there.
  const isPipelineSurfaceActive = useIsPipelineSurfaceActive();
  // SE-6936: role gate for `requiresManagerPlus` items (Squad). Fail-closed —
  // see `isNavItemVisible`.
  const isManagerPlus = useIsManagerPlus();
  // Before PostHog identifies the user, `/flags` is evaluated for the
  // ANONYMOUS distinct id and a user-targeted flag resolves to a real `false`.
  // Treating that as decisive drops the tab, and dropping the tab the user is
  // ON navigates them off it — which is why a hard refresh on /squad landed on
  // Home. Report the flag as unresolved until identify settles so the
  // optimistic-visible path above holds through that window. (SE-6233)
  const isPostHogIdentified = usePostHogIdentified();
  const flagValues: FlagValues = useMemo(
    () =>
      new Map<string, boolean | string | undefined>([
        [
          POSTHOG_FLAGS.SQUAD_ROLE_GATING,
          isPostHogIdentified ? squadRoleGatingValue : undefined,
        ],
      ]),
    [squadRoleGatingValue, isPostHogIdentified]
  );

  const allItems: readonly NavItem[] = useMemo(
    () =>
      // SE-5509 injects Pipeline after Scouting when that surface is active;
      // otherwise the static config is used untouched, so nav is unchanged in
      // production.
      //
      // SE-7326 removed the second injection that used to sit beside this one.
      // Fixture Planning gave up its rail slot to become a Scouting tab, gated
      // inside the Scouting screen — which makes nav byte-identical for gated
      // and ungated users alike, since there is no longer a branch that could
      // get it wrong. This was a `resolveNavConfig` helper until that second
      // injection went; one boolean parameter left is a selector parameter,
      // and the ternary reads better here than a helper does.
      (isPipelineSurfaceActive
        ? insertAfter(NAV_CONFIG, "scouting", PIPELINE_NAV_ITEM)
        : NAV_CONFIG
      )
        .filter(config =>
          isNavItemVisible(config, isDefaultOrgUser, flagValues, isManagerPlus)
        )
        .map(config => ({
          ...config,
          isActive: checkIsActive(
            config.path,
            pathname,
            navigationSource,
            isPipelineSurfaceActive
          ),
          // The badge renders whenever an item declares `badgeText`. No item
          // declares one today — Squad, the last consumer, left beta in
          // SE-7642 — but the plumbing is deliberately kept for the next
          // surface that ships behind a badge. (The `squad-beta-badge`
          // PostHog flag was retired earlier, in SE-5228, at 100%.)
          badge: config.badgeText,
        })),
    [
      pathname,
      navigationSource,
      isDefaultOrgUser,
      flagValues,
      isPipelineSurfaceActive,
      isManagerPlus,
    ]
  );

  const tabItems: readonly NavItem[] = useMemo(
    () =>
      allItems.filter(item =>
        isSmallScreen ? item.showInTabsSmallScreen : item.showInTabsLargeScreen
      ),
    [allItems, isSmallScreen]
  );

  const sidebarItems: readonly NavItem[] = useMemo(
    () => allItems.filter(item => item.showInSidebar),
    [allItems]
  );

  const sidebarBottomItems: readonly NavItem[] = useMemo(
    () => allItems.filter(item => item.showInSidebarBottom),
    [allItems]
  );

  // SE-5508 `tier_block_render`, nav surface. The Pipeline entry is withheld
  // from a below-Manager user by simply never being injected into the config
  // above, so the denial has no rendered element to hang telemetry off — this
  // reports it. The emitter's once-per-session guard is module-level precisely
  // because this hook has two simultaneous consumers (the tabs layout and
  // LeftSidebar); an instance-scoped guard would double-count every
  // wide-viewport session.
  usePipelineTierBlock("nav");

  return {
    allItems,
    tabItems,
    sidebarItems,
    sidebarBottomItems,
    isSmallScreen,
  };
}

export default useNavItems;
export { checkIsActive, NAV_CONFIG, PIPELINE_NAV_ITEM };
export type { NavItem, NavItemConfig, UseNavItemsReturn };
